import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError, thumb } from "../lib/api";
import { Calendar } from "../components/ui/calendar";
import { toast } from "sonner";
import { Star, ArrowRight, PlayCircle, ShoppingBag, Leaf } from "lucide-react";
import { Link } from "react-router-dom";
import GuestCheckoutModal from "../components/GuestCheckoutModal";
import OpenStudioCountdown from "../components/OpenStudioCountdown";
import { trackInitiateCheckout } from "../lib/metaPixel";

const OS_IMG = "https://images.pexels.com/photos/29845005/pexels-photo-29845005.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function fmt(d) {
  if (!d) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function OpenStudio() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [projects, setProjects] = useState([]);
  const [mine, setMine] = useState(null);
  const [date, setDate] = useState(null);
  const [month, setMonth] = useState(() => new Date());
  const [slot, setSlot] = useState("");
  const [numGuests, setNumGuests] = useState(1);
  const [qty, setQty] = useState({});
  const [avail, setAvail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [guestOpen, setGuestOpen] = useState(false);
  const [kits, setKits] = useState([]);
  const [settings, setSettings] = useState({});
  const [blockedDates, setBlockedDates] = useState([]);

  useEffect(() => {
    const d = params.get("date");
    if (d && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
      const [y, m, day] = d.split("-").map(Number);
      const picked = new Date(y, m - 1, day, 12);
      setDate(picked);
      setMonth(picked);
    }
  }, [params]);

  useEffect(() => {
    api.get("/open-studio/info").then(({ data }) => {
      setInfo(data);
      // Jump the calendar to the opening month if we're still before opening day
      if (data?.opening_date) {
        const [oy, om, od] = data.opening_date.split("-").map(Number);
        const openD = new Date(oy, om - 1, od);
        if (new Date(new Date().setHours(0, 0, 0, 0)) < openD) setMonth(openD);
      }
    }).catch(() => {});
    api.get("/open-studio/projects").then(({ data }) => {
      setProjects(data);
    }).catch(() => {});
    api.get("/products?category=cake_kit").then(({ data }) => {
      const feat = (data || []).filter((k) => k.featured);
      setKits((feat.length ? feat : (data || [])).slice(0, 3));
    }).catch(() => {});
    api.get("/settings").then(({ data }) => setSettings(data || {})).catch(() => {});
    api.get("/open-studio/blocked-dates").then(({ data }) => setBlockedDates(data?.dates || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user || user === false) { setMine(null); return; }
    api.get("/membership/me").then(({ data }) => setMine(data)).catch(() => setMine(null));
  }, [user]);

  useEffect(() => {
    setSlot("");
    if (!date) { setAvail(null); return; }
    api.get(`/open-studio/availability?date=${fmt(date)}`).then(({ data }) => setAvail(data)).catch(() => setAvail(null));
  }, [date]);

  const isMember = mine?.active;
  const freeLeft = mine?.free_visits_remaining ?? 0;
  const useFree = isMember && freeLeft > 0;

  const selected = projects
    .map((p) => ({ ...p, q: qty[p.name] || 0 }))
    .filter((p) => p.q > 0);
  const totalQty = selected.reduce((n, p) => n + p.q, 0);
  const subtotal = selected.reduce((s, p) => s + p.price * p.q, 0);
  const freeCredit = useFree && selected.length ? Math.max(...selected.map((p) => p.price)) : 0;
  const memberDiscount = isMember ? +((subtotal - freeCredit) * 0.15).toFixed(2) : 0;
  const total = Math.max(0, +(subtotal - freeCredit - memberDiscount).toFixed(2));

  const setQ = (name, delta) =>
    setQty((prev) => ({ ...prev, [name]: Math.max(0, (prev[name] || 0) + delta) }));

  const book = async () => {
    if (!date) { toast.error("Pick a date"); return; }
    if (!slot) { toast.error("Pick an arrival time"); return; }
    if (totalQty < 1) { toast.error("Add at least one decorating experience"); return; }
    if (!user || user === false) { setGuestOpen(true); return; }
    doBook(null);
  };

  const doBook = async (guest) => {
    setBusy(true);
    try {
      const experiences = selected.map((p) => ({ name: p.name, qty: p.q }));
      const bookingPayload = { booking_date: fmt(date), time_slot: slot, num_guests: numGuests, experiences };
      if (guest) { bookingPayload.guest_name = guest.name; bookingPayload.guest_email = guest.email; }
      const { data } = await api.post("/open-studio/bookings", bookingPayload);
      if (data.free) {
        toast.success("Table reserved with your free member visit!");
        nav(user && user !== false ? "/dashboard" : "/");
        return;
      }
      const ckPayload = {
        item_type: "open_studio", item_id: data.booking_id, origin_url: window.location.origin,
      };
      trackInitiateCheckout({ value: total, numItems: totalQty, name: "Open Studio reservation" });
      if (guest) { ckPayload.guest_name = guest.name; ckPayload.guest_email = guest.email; }
      const { data: ck } = await api.post("/payments/checkout", ckPayload);
      window.location.href = ck.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };

  const slots = info?.time_slots || [];

  return (
    <div data-testid="open-studio-page" className="pt-32 pb-24 fade-in bg-[#F2E9D8]">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16">
        <div className="mb-12">
          <OpenStudioCountdown openingDate={info?.opening_date} showCta={false} />
        </div>
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Intro */}
          <div>
            <p className="text-xs tracking-[0.3em] uppercase font-bold mb-4">Reserve your creative time</p>
            <h1 data-testid="open-studio-title" className="font-serif font-black text-5xl sm:text-7xl text-[#121212] leading-[0.95]">
              Open <span className="lime-swipe italic">Studio.</span>
            </h1>
            <p className="mt-8 text-lg text-[#121212]/80 leading-relaxed">
              Open Studio isn't a class—it's your own creative time at Cake Society. Reserve a table,
              choose one or more decorating experiences, and we'll have everything prepared before you
              arrive. Whether you're coming with friends, family, your kids, or for a date, we'll have
              your station ready when you walk in. Open Studio runs <strong>every Wednesday, Thursday &amp; Friday</strong>.
              Walk-ins are always welcome when tables are available.
            </p>
            <div className="mt-8 rounded-none overflow-hidden aspect-[4/3] border-2 border-[#121212]">
              <img src={thumb(settings.open_studio_image || OS_IMG, 940)} alt="Open Studio" className="w-full h-full object-cover" />
            </div>
            {!isMember && (
              <div className="mt-8 bg-[#121212] text-[#F2E9D8] p-6">
                <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#C6FF33]">Members save more</p>
                <p className="mt-2 text-sm">Cake Society members get <strong>1 free visit every month</strong> plus 15% off extra visits.{" "}
                  <button onClick={() => nav("/membership")} className="underline font-bold" data-testid="os-join-link">Become a member →</button>
                </p>
              </div>
            )}
          </div>

          {/* Booking panel */}
          <div className="bg-white border-2 border-[#121212] p-8">
            <h2 className="font-serif font-black text-3xl text-[#121212]">Reserve Your Table</h2>
            <p className="text-sm text-[#121212]/60 mt-1">One reservation = one table (up to 4 guests) for a 2-hour session.</p>
            {useFree && (
              <div className="mt-3 bg-[#C6FF33] border-2 border-[#121212] px-4 py-2 text-xs tracking-[0.15em] uppercase font-bold" data-testid="free-visit-banner">
                ✓ One experience is free with your membership ({freeLeft} visit left this month)
              </div>
            )}

            <div className="mt-6 space-y-5">
              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-bold block mb-2">Pick a date <span className="text-[#121212]/50 normal-case tracking-normal font-normal">— Wednesdays, Thursdays &amp; Fridays only</span></label>
                <div className="border-2 border-[#121212] p-2 inline-block">
                  <Calendar mode="single" selected={date} onSelect={setDate}
                    month={month} onMonthChange={setMonth}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0)) || (info?.opening_date && fmt(d) < info.opening_date) || (d.getDay() !== 3 && d.getDay() !== 4 && d.getDay() !== 5) || blockedDates.includes(fmt(d))}
                    data-testid="open-studio-calendar" />
                </div>
              </div>

              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-bold block mb-2">Time slot</label>
                <div className="grid grid-cols-1 gap-2">
                  {slots.map((s) => {
                    const remaining = avail?.remaining?.[s];
                    const openingClosed = info?.opening_date && date && fmt(date) === info.opening_date && s < (info.opening_first_slot || "");
                    const isBlocked = avail?.blocked_slots?.includes(s);
                    const full = (date && remaining === 0) || openingClosed;
                    return (
                      <button
                        key={s}
                        data-testid={`slot-${s.replace(/[: ]/g, "")}`}
                        disabled={full}
                        onClick={() => setSlot(s)}
                        className={`px-4 py-3 border-2 border-[#121212] text-left text-sm font-bold flex justify-between items-center disabled:opacity-40 disabled:cursor-not-allowed ${slot === s ? "bg-[#C6FF33]" : "bg-white"}`}
                      >
                        <span>{s}</span>
                        {date && remaining != null && (
                          <span className="text-xs tracking-[0.1em] uppercase">{isBlocked ? "Unavailable" : openingClosed ? "Opens 2 PM" : full ? "Full" : `${remaining} left`}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {!date && <p className="text-xs text-[#121212]/60 mt-2">Pick a date to see availability.</p>}
              </div>

              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-bold block mb-2">Number of guests</label>
                <div className="grid grid-cols-4 gap-2">
                  {[1, 2, 3, 4].map((n) => (
                    <button
                      key={n}
                      data-testid={`guests-${n}`}
                      onClick={() => setNumGuests(n)}
                      className={`px-3 py-3 border-2 border-[#121212] text-sm font-bold ${numGuests === n ? "bg-[#C6FF33]" : "bg-white"}`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-[#121212]/55 mt-2">Groups can share a cake or decorate individually — you don't need one per guest.</p>
              </div>

              <div>
                <label className="text-xs tracking-[0.2em] uppercase font-bold block mb-2">Choose your decorating experiences</label>
                <p data-testid="no-dye-note" className="text-xs text-[#121212]/70 mb-3 border-l-2 border-[#C6FF33] pl-3 leading-snug">
                  Looking for a no-artificial-dye option? We keep at least one Open Studio project available without artificial food dyes. Look for the badge on the project card.
                </p>
                <div className="space-y-3">
                  {projects.map((p) => (
                    <div key={p.id} data-testid={`experience-${p.id}`} className="border-2 border-[#121212] bg-white overflow-hidden flex">
                      <div className="w-28 shrink-0 aspect-square overflow-hidden border-r-2 border-[#121212]">
                        {p.image_url && <img src={thumb(p.image_url, 300)} alt={p.name} className="w-full h-full object-cover" />}
                      </div>
                      <div className="p-3 flex-1 flex flex-col justify-between">
                        <div>
                          <div className="flex items-center justify-between gap-2">
                            <p className="font-serif font-black text-lg text-[#121212] leading-tight">{p.emoji} {p.name}</p>
                            <span className="font-serif font-black text-[#121212] whitespace-nowrap">${p.price}</span>
                          </div>
                          {p.no_dyes && (
                            <span data-testid={`no-dye-badge-${p.id}`} className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 bg-[#C6FF33] text-[#121212] text-[10px] tracking-[0.1em] uppercase font-bold border border-[#121212]">
                              <Leaf size={11} strokeWidth={2.5} /> No Artificial Food Dyes
                            </span>
                          )}
                          {p.description && <p className="text-xs text-[#121212]/70 mt-1 leading-snug">{p.description}</p>}
                        </div>
                        <div className="flex items-center gap-3 mt-2">
                          <button data-testid={`exp-dec-${p.id}`} onClick={() => setQ(p.name, -1)} className="w-8 h-8 border-2 border-[#121212] font-bold hover:bg-[#F2E9D8]">−</button>
                          <span data-testid={`exp-qty-${p.id}`} className="w-6 text-center font-bold">{qty[p.name] || 0}</span>
                          <button data-testid={`exp-inc-${p.id}`} onClick={() => setQ(p.name, 1)} className="w-8 h-8 border-2 border-[#121212] font-bold hover:bg-[#F2E9D8]">+</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-8 border-t-2 border-[#121212] pt-5 space-y-1.5">
              {selected.length > 0 && (
                <div className="flex justify-between text-sm text-[#121212]/70">
                  <span>Subtotal</span><span data-testid="os-subtotal">${subtotal.toFixed(2)}</span>
                </div>
              )}
              {freeCredit > 0 && (
                <div className="flex justify-between text-sm text-[#121212]/70">
                  <span>Free member visit</span><span data-testid="os-free-credit">−${freeCredit.toFixed(2)}</span>
                </div>
              )}
              {memberDiscount > 0 && (
                <div className="flex justify-between text-sm text-[#121212]/70">
                  <span>Member discount (15%)</span><span data-testid="os-member-discount">−${memberDiscount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex items-center justify-between pt-1">
                <span className="text-xs tracking-[0.2em] uppercase font-bold">Total</span>
                <span className="font-serif font-black text-3xl text-[#121212]" data-testid="os-total">
                  {total <= 0 && selected.length ? "FREE" : `$${total.toFixed(2)}`}
                </span>
              </div>
            </div>

            <button
              data-testid="book-open-studio-btn"
              onClick={book} disabled={busy}
              className="mt-5 w-full px-8 py-4 bg-[#121212] text-[#F2E9D8] hover:bg-black disabled:opacity-40 text-xs tracking-[0.25em] uppercase font-bold inline-flex items-center justify-center gap-3 transition-all"
            >
              <Star size={16} fill="#C6FF33" stroke="#C6FF33" />
              {busy ? "Loading…" : total <= 0 && selected.length ? "Reserve free table" : "Reserve & pay"}
            </button>

            <div data-testid="reservation-policy" className="mt-6 border-2 border-[#121212] bg-[#F5EFE6] p-5">
              <p className="font-serif font-black text-lg text-[#121212]">Drop-In Studio Reservation Policy</p>
              <div className="mt-2 space-y-2 text-sm text-[#121212]/75 leading-relaxed">
                <p>Reservations are nonrefundable but may be moved to another available date or time with at least 24 hours' notice. Changes made within 24 hours and no-shows cannot be accommodated.</p>
                <p>For members, a missed complimentary visit or late cancellation will be considered used for that month.</p>
                <p>Walk-ins are welcome based on availability, but reservations are recommended.</p>
              </div>
            </div>
          </div>
        </div>

        {kits.length > 0 && (
          <div data-testid="os-take-home" className="mt-16 pt-12 border-t-2 border-[#121212]">
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <div>
                <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#121212] mb-2">Can't make it in?</p>
                <h2 className="font-serif font-black text-3xl sm:text-4xl text-[#121212]">Take the project home.</h2>
                <p className="mt-3 text-stone-600 max-w-xl">Prefer to decorate at your own table? Grab the matching Cake Kit To-Go — or follow along with the online tutorial.</p>
              </div>
              <Link to="/shop" data-testid="os-shop-all-kits" className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#121212] hover:gap-3 transition-all">
                All cake kits <ArrowRight size={14} />
              </Link>
            </div>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {kits.map((k) => (
                <div key={k.id} data-testid={`os-kit-${k.id}`} className="bg-white border-2 border-[#121212] flex flex-col">
                  <div className="aspect-[4/3] overflow-hidden border-b-2 border-[#121212]">
                    <img src={thumb(k.image_url, 600)} alt={k.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <h3 className="font-serif font-black text-xl text-[#121212] leading-tight">{k.title}</h3>
                      <span className="font-serif font-black text-xl text-[#121212] whitespace-nowrap">${k.price}</span>
                    </div>
                    {k.description && <p className="mt-2 text-sm text-stone-600 line-clamp-2">{k.description}</p>}
                    <div className="mt-auto pt-4 flex flex-col gap-2">
                      <Link to="/shop" data-testid={`os-kit-shop-${k.id}`} className="inline-flex items-center justify-between px-4 py-2.5 bg-[#121212] text-[#F2E9D8] text-[11px] tracking-[0.18em] uppercase font-bold hover:bg-black transition-colors">
                        <span className="inline-flex items-center gap-2"><ShoppingBag size={14} /> Shop the kit</span><ArrowRight size={13} />
                      </Link>
                      {k.tutorial_id && (
                        <Link to={`/tutorials/${k.tutorial_id}`} data-testid={`os-kit-tutorial-${k.id}`} className="inline-flex items-center justify-between px-4 py-2.5 border-2 border-[#121212] text-[#121212] text-[11px] tracking-[0.18em] uppercase font-bold hover:bg-[#121212] hover:text-[#F2E9D8] transition-colors">
                          <span className="inline-flex items-center gap-2"><PlayCircle size={14} /> Watch the tutorial</span><ArrowRight size={13} />
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <GuestCheckoutModal
        open={guestOpen}
        onClose={() => setGuestOpen(false)}
        onSubmit={(g) => doBook(g)}
        busy={busy}
        nextPath="/open-studio"
      />
    </div>
  );
}
